/**
 * VISION (wholesale) lender scoping.
 *
 * A wholesale Account Executive is tied to a Lender. On the loan-option pages
 * (LE Price Eng, LE Price Eng Options, Summary Sheet) they should see only the
 * loan options whose LE Price Eng "Lender" field matches their assigned lender —
 * so a UWM AE sees the UWM options and a Kind Lending AE sees the Kind options
 * on the very same broker file. A blank assigned lender = see all (retail /
 * unassigned users are unaffected).
 */

export function assigned_lender(accountProfile) {
  return ((accountProfile && accountProfile.assigned_lender) || "").trim();
}

/**
 * True if this loan option should be shown to the viewer. Matches the loan's
 * Lender field to the viewer's assigned lender, case-insensitive and trimmed.
 * When the viewer has no assigned lender, every loan is visible.
 */
export function loan_visible_for_viewer(loan, accountProfile) {
  // Lender filtering applies ONLY to wholesale (VISION) viewers. A retail LO —
  // or anyone who is not wholesale — always sees every loan, even if their
  // profile somehow carries a stray assigned_lender. This is the guard that
  // keeps a bad/contaminated profile from ever blanking a retail loan file.
  if (!is_wholesale_view_only(accountProfile)) {
    return true;
  }
  // Setup/edit-unlock mode: show every loan so an admin can see and configure
  // them (assign the Lender field). The filter is a VIEW feature, not an edit one.
  if (edit_unlocked()) {
    return true;
  }
  const lender = assigned_lender(accountProfile);
  if (!lender) {
    return true;
  }
  const loanLender = ((loan && loan.lender) || "").trim();
  return loanLender.toLowerCase() === lender.toLowerCase();
}

/**
 * Wholesale (VISION) users get view-only loan files across the whole chain.
 * Detected from product = "Wholesale", with the Account Executive role as a
 * fallback for records that predate the product field.
 */
export function is_wholesale_view_only(accountProfile) {
  if (!accountProfile) {
    return false;
  }
  if ((accountProfile.product || "") === "Wholesale") {
    return true;
  }
  return /account executive/i.test(accountProfile.management_role || "");
}

/**
 * The 5 Summary-Sheet loan slots, with any loan the viewer can't see blanked to
 * undefined. The summary tables already render undefined slots as empty columns,
 * so this hides other lenders' columns everywhere at once while keeping each
 * loan in its original column (Loan 1/3/5 stay put).
 */
export function visible_five_loans(loans, accountProfile) {
  const list = loans || [];
  return [0, 1, 2, 3, 4].map(i =>
    loan_visible_for_viewer(list[i], accountProfile) ? list[i] : undefined
  );
}

/** True when the demo edit-unlock (setup) flag is set for this browser. */
export function edit_unlocked() {
  return (
    typeof window !== "undefined" &&
    window.localStorage.getItem("demo_edit_unlock") === "1"
  );
}

/**
 * Wholesale view-only is *active* (restrictions apply) only when the user is a
 * wholesale viewer AND editing isn't unlocked. Use this to hide pages/controls
 * (Down Payment, Summary) so setup mode still shows everything.
 */
export function is_view_only_active(accountProfile) {
  return is_wholesale_view_only(accountProfile) && !edit_unlocked();
}
